import { FCElement } from '@fc/component';
import { onAnimationEnd } from '@fc/utilities';
import { FCEvents, getCartCountSyncData } from '@fc/events';

const STORAGE_KEY = 'cart-count';
const STALE_MS = 10_000;
const MAX_DISPLAY = 100;

function toCount(raw) {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return isNaN(n) ? 0 : Math.max(0, n | 0);
}

function displayCount(count) {
  const c = toCount(count);
  if (c === 0) return '';
  return c < MAX_DISPLAY ? String(c) : '99+';
}

function countSize(count) {
  const c = toCount(count);
  if (c === 0) return 'empty';
  if (c >= MAX_DISPLAY) return 'overflow';
  return c > 9 ? 'double' : 'single';
}

class CartIcon extends FCElement {
  requiredRefs = ['cartBadge', 'cartBadgeText', 'cartBadgeCount', 'cartBadgeAccessibleCount'];
  #eventsController = null;
  #committedCount = null;
  #pendingDeltas = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#bindEvents();
    this.#hydrateFromStorage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventsController?.abort();
    this.#eventsController = null;
  }

  get displayedCount() {
    const countElement = this.refs.cartBadgeCount;
    return toCount(countElement?.dataset.count ?? countElement?.textContent);
  }

  set displayedCount(val) {
    const c = toCount(val);
    if (this.refs.cartBadgeCount) {
      this.refs.cartBadgeCount.dataset.count = String(c);
      this.refs.cartBadgeCount.dataset.displayCount = displayCount(c);
      this.refs.cartBadgeCount.textContent = displayCount(c);
    }

    if (this.refs.cartBadge) {
      this.refs.cartBadge.dataset.countSize = countSize(c);
    }
    this.#updateAccessibleCount(c);
  }

  #bindEvents() {
    this.#eventsController?.abort();
    this.#eventsController = new AbortController();
    document.addEventListener(FCEvents.cartRefresh, this.#onCartRefresh, {
      signal: this.#eventsController.signal,
      passive: true,
    });
    document.addEventListener(FCEvents.cartCountSync, this.#onCartCountSync, {
      signal: this.#eventsController.signal,
      passive: true,
    });
  }

  #onCartRefresh = async (event) => {
    const itemCount = toCount(event?.detail?.data?.itemCount);
    const shouldAnimate = itemCount !== this.displayedCount;
    this.#commitCount(itemCount);
    await this.#renderBadgeState(this.#effectiveCount(), { animate: shouldAnimate, persist: true });
  };

  #onCartCountSync = async (event) => {
    const data = getCartCountSyncData(event);
    if (!data.operationId) return;

    if (data.phase === 'start') {
      this.#pendingDeltas.set(data.operationId, data.delta);
      const nextCount = this.#effectiveCount();
      await this.#renderBadgeState(nextCount, {
        animate: nextCount !== this.displayedCount,
        persist: true,
      });
      return;
    }

    this.#pendingDeltas.delete(data.operationId);
    if (data.count != null) {
      this.#commitCount(data.count);
    }

    const nextCount = this.#effectiveCount();
    await this.#renderBadgeState(nextCount, {
      animate: data.didError !== true && nextCount !== this.displayedCount,
      persist: true,
    });
  };

  async #renderBadgeState(count, options = {}) {
    const animate = options.animate !== false;
    const persist = options.persist !== false;
    const { cartBadge, cartBadgeText, cartBadgeCount } = this.refs;
    if (!cartBadge || !cartBadgeCount) return;

    const empty = count === 0;
    cartBadgeCount.classList.toggle('hidden', empty);
    cartBadge.classList.toggle('visually-hidden', empty);

    const showAnim = count > 0 && animate;
    cartBadge.classList.toggle('cart-badge--animating', showAnim);

    this.displayedCount = count;
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    if (persist) {
      this.#persistCount(count);
    }

    if (!animate) return;

    await onAnimationEnd(cartBadgeText);
    cartBadge.classList.remove('cart-badge--animating');
  }

  renderCartBadge = (itemCount, _additive = false, shouldAnimate = true) => {
    this.#commitCount(itemCount);
    return this.#renderBadgeState(this.#effectiveCount(), {
      animate: shouldAnimate,
      persist: true,
    });
  };

  #persistCount(count) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        }),
      );
    } catch {
      /* storage unavailable */
    }
  }

  #hydrateFromStorage() {
    const storedCount = this.#readStoredCount();
    // eslint-disable-next-line eqeqeq
    if (storedCount == null) return;
    this.#commitCount(storedCount);
    void this.#renderBadgeState(storedCount, { animate: false, persist: false });
  }

  #readStoredCount() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!data?.value || !data?.timestamp) return null;
      if (Date.now() - data.timestamp >= STALE_MS) return null;

      const storedCount = toCount(data.value);
      if (String(storedCount) === String(this.refs.cartBadgeCount?.dataset.count ?? '')) {
        return null;
      }

      return storedCount;
    } catch {
      return null;
    }
  }

  #commitCount(count) {
    this.#committedCount = toCount(count);
  }

  #updateAccessibleCount(count) {
    const target = this.refs.cartBadgeAccessibleCount;
    if (!(target instanceof HTMLElement)) return;

    const label = target.dataset.label || '';
    const nextText = `${label}: ${toCount(count)}`;
    if (target.textContent?.trim() !== nextText) {
      target.textContent = nextText;
    }
  }

  #effectiveCount() {
    const committed = this.#committedCount ?? this.displayedCount;
    let pendingDelta = 0;

    for (const delta of this.#pendingDeltas.values()) {
      pendingDelta += toCount(delta) ?? 0;
    }

    return Math.max(0, committed + pendingDelta);
  }
}

if (!customElements.get('cart-badge-icon')) {
  customElements.define('cart-badge-icon', CartIcon);
}
